/**
 * SharePoint root browser + folder bookmarks for a project or estimate.
 * Personal by default. Share to selected teammates (or everyone); they accept or refuse.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { toSharePointHref } from '../../utils/sharepointUrl';
import { bookmarkUrlKey } from '../../utils/bookmarkUrl';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import SharePointFolderBrowser from './SharePointFolderBrowser';
import SharePointAssistant from './SharePointAssistant';
import AutodeskBuildModules from './AutodeskBuildModules';

const emptyForm = { label: '', url: '' };

const BRIDGES = {
  autodesk_build: {
    productName: 'Autodesk Build',
    api: 'autodesk-build',
    helpId: 'autodesk-build-help',
    placeholder: 'https://acc.autodesk.com/build/home/projects/…',
    hint: 'Copy the project URL from Autodesk Build (Home). Shortcuts include Files, Sheets, Issues, RFIs. Colour-coding lives on Schedule → Model, not here.',
    openLabel: 'Open Autodesk Build project',
    noRoot: 'No Autodesk Build project linked yet — paste the ACC URL below.',
    sub: 'Door into the client ACC project for RFIs, issues, sheets, and files. Progress colour stays on Schedule → Model.',
    crumbs: 'Autodesk Build tools',
    empty: 'Paste the Autodesk Build project URL above (from acc.autodesk.com). We list Home, Files, Sheets, Issues, RFIs, and the other Build tools.',
    saved: 'Autodesk Build project link saved.',
    cleared: 'Autodesk Build project link cleared.',
    label: 'Autodesk Build project URL',
  },
  procore: {
    productName: 'Procore',
    api: 'procore',
    helpId: 'procore-help',
    placeholder: 'https://app.procore.com/…/project/home',
    hint: 'Copy the project Home URL from Procore. Shortcuts include RFIs, Drawings, Documents, Submittals. The portal does not colour their model viewer.',
    openLabel: 'Open Procore project',
    noRoot: 'No Procore project linked yet — paste the project URL below.',
    sub: 'Door into the client Procore project for RFIs, drawings, and documents. Progress colour stays on Schedule → Model.',
    crumbs: 'Procore tools',
    empty: 'Paste the Procore project URL above. We list Home, Documents, Drawings, RFIs, and the other tools.',
    saved: 'Procore project link saved.',
    cleared: 'Procore project link cleared.',
    label: 'Procore project URL',
  },
};

const SharePointBookmarksPage = ({
  projectId = null,
  estimateId = null,
  rootUrl = null,
  rootLabel = 'Open SharePoint',
  provider = 'sharepoint',
}) => {
  const { instance, accounts } = useMsal();
  const fileInputRef = useRef(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [pending, setPending] = useState([]);
  const [members, setMembers] = useState([]);
  const [resolvedRootUrl, setResolvedRootUrl] = useState(rootUrl);
  const [browseRootUrl, setBrowseRootUrl] = useState(null);
  const [browseLabel, setBrowseLabel] = useState('Root');
  const [onBookmark, setOnBookmark] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [shareModal, setShareModal] = useState(null); // { mode: 'one'|'all', bookmark?, count? }
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);
  const [accRootDraft, setAccRootDraft] = useState('');
  const [accModules, setAccModules] = useState([]);
  const [savingRoot, setSavingRoot] = useState(false);

  const bridge = BRIDGES[provider] || null;
  const isBridge = Boolean(bridge);
  const productName = bridge?.productName || 'SharePoint';

  const scopeQuery = projectId
    ? `projectId=${projectId}&provider=${provider}`
    : estimateId
      ? `estimateId=${estimateId}&provider=${provider}`
      : null;

  const scopeBody = projectId
    ? { projectId, provider }
    : { estimateId, provider };
  const scopeNoun = projectId ? 'project' : 'estimate';

  useEffect(() => {
    if (isBridge) return;
    const href = toSharePointHref(rootUrl);
    setResolvedRootUrl(href);
    if (!onBookmark) {
      setBrowseRootUrl(href);
      setBrowseLabel('Root');
    }
  }, [rootUrl, onBookmark, isBridge]);

  const fetchAll = useCallback(async () => {
    if (!scopeQuery || !accounts[0]) return;
    setLoading(true);
    setError('');
    try {
      const [bmRes, pendingRes, membersRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/sharepoint-bookmarks?${scopeQuery}`),
        authenticatedApiCall(instance, accounts, 'GET', `/sharepoint-bookmarks/pending?${scopeQuery}`),
        authenticatedApiCall(instance, accounts, 'GET', `/sharepoint-bookmarks/members?${scopeQuery}`),
      ]);
      setBookmarks(bmRes.data.bookmarks || []);
      setPending(pendingRes.data.invites || []);
      setMembers(membersRes.data.members || []);
    } catch (err) {
      console.error('Error loading SharePoint bookmarks:', err);
      setError(err.response?.data?.message || 'Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, scopeQuery]);

  useEffect(() => {
    if (!accounts[0]) return;
    let cancelled = false;
    (async () => {
      try {
        let href = null;
        if (isBridge) return;
        if (projectId) {
          const res = await authenticatedApiCall(instance, accounts, 'GET', `/projects/${projectId}`);
          href = toSharePointHref(res.data?.project?.sharepoint_url || rootUrl);
        } else if (estimateId) {
          const res = await authenticatedApiCall(instance, accounts, 'GET', `/estimates/${estimateId}`);
          href = toSharePointHref(res.data?.estimate?.sharepoint_url || rootUrl);
        }
        if (!cancelled && href !== undefined) {
          setResolvedRootUrl(href);
          if (!onBookmark) {
            setBrowseRootUrl(href);
            setBrowseLabel('Root');
          }
        }
      } catch (err) {
        console.error('Error loading SharePoint root URL:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [instance, accounts, projectId, estimateId, rootUrl, onBookmark, isBridge]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!isBridge || !scopeQuery || !accounts[0]) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', `/${bridge.api}/root?${scopeQuery}`);
        if (cancelled) return;
        const url = res.data?.url || null;
        setResolvedRootUrl(url);
        setAccRootDraft(url || '');
        setAccModules(res.data?.modules || []);
      } catch (err) {
        console.error(`Error loading ${productName} root:`, err);
      }
    })();
    return () => { cancelled = true; };
  }, [instance, accounts, isBridge, bridge, scopeQuery, productName]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (bm) => {
    setEditingId(bm.id);
    setForm({
      label: bm.label || '',
      url: bm.url || '',
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.url.trim()) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      if (editingId) {
        await authenticatedApiCall(instance, accounts, 'PUT', `/sharepoint-bookmarks/${editingId}`, {
          label: form.label.trim(),
          url: form.url.trim(),
        });
      } else {
        await authenticatedApiCall(instance, accounts, 'POST', '/sharepoint-bookmarks', {
          label: form.label.trim(),
          url: form.url.trim(),
          provider,
          ...scopeBody,
        });
      }
      resetForm();
      await fetchAll();
    } catch (err) {
      console.error('Error saving bookmark:', err);
      setError(err.response?.data?.message || 'Failed to save bookmark');
    } finally {
      setSaving(false);
    }
  };

  const saveAccRoot = async (e) => {
    e.preventDefault();
    setSavingRoot(true);
    setError('');
    setNotice('');
    try {
      const res = await authenticatedApiCall(instance, accounts, 'PUT', `/${bridge.api}/root`, {
        ...scopeBody,
        url: accRootDraft.trim(),
      });
      const url = res.data?.url || null;
      setResolvedRootUrl(url);
      setAccRootDraft(url || '');
      setAccModules(res.data?.modules || []);
      setNotice(url ? bridge.saved : bridge.cleared);
    } catch (err) {
      console.error(`Error saving ${productName} root:`, err);
      setError(err.response?.data?.message || `Failed to save ${productName} link`);
    } finally {
      setSavingRoot(false);
    }
  };

  const handleQuickBookmark = async (item) => {
    const url = item?.webUrl || item?.url;
    const label = (item?.name || item?.label || 'Bookmark').slice(0, 200);
    if (!url) return;
    const key = bookmarkUrlKey(url);
    const mine = bookmarks.find((b) => b.is_mine && bookmarkUrlKey(b.url) === key);
    setBookmarking(true);
    setError('');
    setNotice('');
    try {
      if (mine) {
        await authenticatedApiCall(instance, accounts, 'DELETE', `/sharepoint-bookmarks/${mine.id}`);
        if (editingId === mine.id) resetForm();
        setNotice(`Removed bookmark “${mine.label}”.`);
      } else {
        await authenticatedApiCall(instance, accounts, 'POST', '/sharepoint-bookmarks', {
          label,
          url,
          provider,
          ...scopeBody,
        });
        setNotice(`Bookmarked “${label}”.`);
      }
      await fetchAll();
    } catch (err) {
      console.error('Error toggling bookmark:', err);
      setError(err.response?.data?.message || 'Failed to update bookmark');
    } finally {
      setBookmarking(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this bookmark?')) return;
    setError('');
    setNotice('');
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/sharepoint-bookmarks/${id}`);
      if (editingId === id) resetForm();
      await fetchAll();
    } catch (err) {
      console.error('Error deleting bookmark:', err);
      setError(err.response?.data?.message || 'Failed to delete bookmark');
    }
  };

  const openShareModal = (bm) => {
    setShareModal({ mode: 'one', bookmark: bm });
    setSelectedUserIds([]);
  };

  const openShareAllModal = () => {
    const count = personalOnly.length;
    if (!count) {
      setError('Add at least one personal bookmark before sharing all.');
      return;
    }
    setShareModal({ mode: 'all', count });
    setSelectedUserIds([]);
  };

  const closeShareModal = () => {
    setShareModal(null);
    setSelectedUserIds([]);
  };

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const selectAllMembers = () => {
    setSelectedUserIds(members.map((m) => m.id));
  };

  const sendShare = async ({ everyone = false } = {}) => {
    if (!shareModal) return;
    if (!everyone && selectedUserIds.length === 0) {
      setError('Pick at least one teammate, or use Share with everyone.');
      return;
    }
    setSharing(true);
    setError('');
    setNotice('');
    try {
      const body = everyone ? { everyone: true } : { userIds: selectedUserIds };
      const res =
        shareModal.mode === 'all'
          ? await authenticatedApiCall(instance, accounts, 'POST', '/sharepoint-bookmarks/share-all-to', {
              ...scopeBody,
              ...body,
            })
          : await authenticatedApiCall(
              instance,
              accounts,
              'POST',
              `/sharepoint-bookmarks/${shareModal.bookmark.id}/share-to`,
              body
            );
      const invited = res.data.invited || 0;
      const pendingCount = res.data.alreadyPending || 0;
      const accepted = res.data.alreadyAccepted || 0;
      const bmCount = res.data.bookmarksShared;
      setNotice(
        (bmCount ? `Shared ${bmCount} bookmark(s). ` : '') +
          `Sent ${invited} invitation(s)` +
          (pendingCount ? `, ${pendingCount} already pending` : '') +
          (accepted ? `, ${accepted} already accepted` : '') +
          '. They will see them only after accepting.'
      );
      closeShareModal();
      await fetchAll();
    } catch (err) {
      console.error('Error sharing bookmark:', err);
      setError(err.response?.data?.message || 'Failed to share bookmark');
    } finally {
      setSharing(false);
    }
  };

  const respondInvite = async (invite, action) => {
    if (!invite?.share_id) return;
    setError('');
    setNotice('');
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/sharepoint-bookmarks/shares/${invite.share_id}/${action}`
      );
      setNotice(
        action === 'accept'
          ? `Accepted “${invite.label}” — it’s now on your SharePoint list.`
          : `Refused “${invite.label}".`
      );
      await fetchAll();
    } catch (err) {
      console.error(`Error ${action}ing invite:`, err);
      setError(err.response?.data?.message || `Failed to ${action} invitation`);
    }
  };

  const copyToMine = async (bm) => {
    setError('');
    setNotice('');
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/sharepoint-bookmarks/${bm.id}/copy`);
      setNotice(`Copied “${bm.label}” to your personal bookmarks.`);
      await fetchAll();
    } catch (err) {
      console.error('Error copying bookmark:', err);
      setError(err.response?.data?.message || 'Failed to copy bookmark');
    }
  };

  const handleExport = async (scope = 'mine') => {
    setError('');
    setNotice('');
    try {
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/sharepoint-bookmarks/export?${scopeQuery}&scope=${scope}`
      );
      const payload = res.data.export;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      const scopeTag = projectId ? `project-${projectId}` : `estimate-${estimateId}`;
      a.href = URL.createObjectURL(blob);
      a.download = `sharepoint-bookmarks-${scopeTag}-${scope}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      setNotice(`Exported ${payload.bookmarks?.length || 0} bookmark(s).`);
    } catch (err) {
      console.error('Error exporting bookmarks:', err);
      setError(err.response?.data?.message || 'Failed to export bookmarks');
    }
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setError('');
    setNotice('');
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const list = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed.bookmarks)
          ? parsed.bookmarks
          : Array.isArray(parsed.export?.bookmarks)
            ? parsed.export.bookmarks
            : null;
      if (!list) throw new Error('JSON must be an array of {label,url} or an export object');

      const res = await authenticatedApiCall(instance, accounts, 'POST', '/sharepoint-bookmarks/import', {
        ...scopeBody,
        provider,
        bookmarks: list,
      });
      setNotice(
        `Imported ${res.data.imported || 0}, updated ${res.data.updated || 0}. Share individually if you want teammates to get them.`
      );
      await fetchAll();
    } catch (err) {
      console.error('Error importing bookmarks:', err);
      setError(err.response?.data?.message || err.message || 'Failed to import bookmarks');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const browseBookmark = (bm) => {
    if (isBridge) {
      if (bm?.url) window.open(bm.url, '_blank', 'noopener,noreferrer');
      return;
    }
    const href = toSharePointHref(bm.url);
    if (!href) return;
    setOnBookmark(true);
    setBrowseRootUrl(href);
    setBrowseLabel(bm.label || 'Bookmark');
  };

  const resetToCrawlerRoot = () => {
    setOnBookmark(false);
    setBrowseRootUrl(resolvedRootUrl);
    setBrowseLabel('Root');
  };

  const personalOnly = bookmarks.filter((b) => b.is_mine);
  const acceptedShared = bookmarks.filter((b) => !b.is_mine);
  const bookmarkedKeys = new Set(bookmarks.map((b) => bookmarkUrlKey(b.url)).filter(Boolean));
  const openHref = (url) => (isBridge ? url : toSharePointHref(url) || url);

  const renderBookmarkRow = (bm, { shared = false } = {}) => (
    <li key={`${bm.id}-${bm.share_id || 'own'}`} style={styles.item}>
      <div style={styles.itemMain}>
        <div style={styles.itemTitleRow}>
          <button type="button" onClick={() => browseBookmark(bm)} style={styles.itemLinkBtn}>
            {bm.label}
          </button>
          {bm.is_mine && <span style={styles.badgeMine}>Personal</span>}
          {shared && (
            <span style={styles.badgeOther}>
              From {bm.shared_by_name || bm.owner_name || bm.owner_email || 'teammate'}
            </span>
          )}
        </div>
        <div style={styles.itemUrl}>{bm.url}</div>
      </div>
      <div style={styles.itemActions}>
        {!isBridge && (
          <button type="button" onClick={() => browseBookmark(bm)} style={styles.secondaryBtn}>
            Browse
          </button>
        )}
        <a
          href={openHref(bm.url)}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...styles.secondaryBtn, textDecoration: 'none', display: 'inline-block' }}
        >
          Open ↗
        </a>
        {bm.is_mine ? (
          <>
            <button type="button" onClick={() => openShareModal(bm)} style={styles.secondaryBtn}>
              Share…
            </button>
            <button type="button" onClick={() => startEdit(bm)} style={styles.secondaryBtn}>
              Edit
            </button>
            <button type="button" onClick={() => handleDelete(bm.id)} style={styles.dangerBtn}>
              Remove
            </button>
          </>
        ) : (
          <button type="button" onClick={() => copyToMine(bm)} style={styles.secondaryBtn}>
            Copy to mine
          </button>
        )}
      </div>
    </li>
  );

  if (!projectId && !estimateId) {
    return (
      <div style={styles.wrap}>
        <p style={styles.muted}>Select a project or estimate to manage {productName} bookmarks.</p>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{productName}</h2>
          <p style={styles.sub}>
            {isBridge
              ? bridge.sub
              : 'Browse folders here in the portal. Preview and download files without leaving. Use Open in SharePoint when you need to edit or co-author. Star a row to bookmark it.'}
          </p>
        </div>
        <div style={styles.headerActions}>
          <button type="button" onClick={() => setShowHelp(true)} style={styles.helpBtn} title="Help">
            ?
          </button>
          {resolvedRootUrl ? (
            <a href={resolvedRootUrl} target="_blank" rel="noopener noreferrer" style={styles.primaryBtn}>
              {isBridge ? bridge.openLabel : rootLabel} ↗
            </a>
          ) : (
            <span style={styles.noRoot}>
              {isBridge
                ? bridge.noRoot
                : 'No root SharePoint link yet — filled overnight by the crawler.'}
            </span>
          )}
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {notice && <div style={styles.notice}>{notice}</div>}

      {isBridge && (
        <form onSubmit={saveAccRoot} style={styles.form}>
          <div style={styles.formRow}>
            <div style={{ ...styles.field, flex: 2 }}>
              <label style={styles.label}>{bridge.label}</label>
              <input
                type="url"
                value={accRootDraft}
                onChange={(e) => setAccRootDraft(e.target.value)}
                placeholder={bridge.placeholder}
                style={styles.input}
              />
            </div>
            <div style={styles.formActions}>
              <button type="submit" disabled={savingRoot} style={styles.primaryBtn}>
                {savingRoot ? 'Saving…' : 'Save project link'}
              </button>
            </div>
          </div>
          <p style={styles.formHint}>{bridge.hint}</p>
        </form>
      )}

      {!isBridge && onBookmark && (
        <div style={styles.browseBanner}>
          Browsing bookmark: <strong>{browseLabel}</strong>
          <button type="button" onClick={resetToCrawlerRoot} style={styles.linkBtn} disabled={!resolvedRootUrl}>
            Back to root folder
          </button>
        </div>
      )}

      {isBridge ? (
        <AutodeskBuildModules
          modules={accModules}
          bookmarkedUrls={bookmarkedKeys}
          onQuickBookmark={handleQuickBookmark}
          bookmarking={bookmarking}
          crumbs={bridge.crumbs}
          emptyHint={bridge.empty}
          openLabel={`Open in ${bridge.productName} ↗`}
        />
      ) : (
        <SharePointFolderBrowser
          key={browseRootUrl || 'no-root'}
          projectId={projectId}
          estimateId={estimateId}
          rootUrl={browseRootUrl}
          rootLabel={browseLabel}
          bookmarkedKeys={bookmarkedKeys}
          onQuickBookmark={handleQuickBookmark}
          bookmarking={bookmarking}
        />
      )}

      <form onSubmit={handleSave} style={styles.form}>
        <div style={styles.formRow}>
          <div style={styles.field}>
            <label style={styles.label}>{editingId ? 'Edit label' : 'Label'}</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
              placeholder="e.g. Drawings, Client schedule"
              maxLength={200}
              style={styles.input}
              required
            />
          </div>
          <div style={{ ...styles.field, flex: 2 }}>
            <label style={styles.label}>{isBridge ? `${bridge.productName} URL` : 'SharePoint URL'}</label>
            <input
              type="url"
              value={form.url}
              onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
              placeholder={
                isBridge
                  ? bridge.placeholder
                  : 'https://laframboisegrpltd.sharepoint.com/...'
              }
              style={styles.input}
              required
            />
          </div>
          <div style={styles.formActions}>
            <button type="submit" disabled={saving} style={styles.primaryBtn}>
              {saving ? 'Saving…' : editingId ? 'Update' : 'Add bookmark'}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} style={styles.secondaryBtn}>
                Cancel
              </button>
            )}
          </div>
        </div>
        <p style={styles.formHint}>
          Bookmarks stay personal until you <strong>Share…</strong> one, or <strong>Share all…</strong> and pick who gets invites.
        </p>
      </form>

      <div style={styles.toolbar}>
        <button
          type="button"
          onClick={openShareAllModal}
          style={styles.primaryBtn}
          disabled={!personalOnly.length}
          title="Invite teammates to all of your bookmarks at once"
        >
          Share all…
        </button>
        <button
          type="button"
          onClick={() => handleExport('mine')}
          style={styles.secondaryBtn}
          title="Download a backup file of your bookmarks (optional)"
        >
          Download backup
        </button>
        <button
          type="button"
          onClick={() => handleExport('all')}
          style={styles.secondaryBtn}
          title="Download a backup of everything you can see here"
        >
          Download all visible
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={styles.secondaryBtn}
          title="Restore bookmarks from a backup file you downloaded earlier"
        >
          Restore backup…
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={(e) => handleImportFile(e.target.files?.[0])}
        />
      </div>

      {pending.length > 0 && (
        <div style={{ ...styles.listCard, marginBottom: 12, border: '1px solid #fcd34d' }}>
          <div style={{ ...styles.listHeader, backgroundColor: '#fffbeb', color: '#92400e' }}>
            Invitations ({pending.length}) — accept to add to your list
          </div>
          <ul style={styles.list}>
            {pending.map((inv) => (
              <li key={inv.share_id} style={styles.item}>
                <div style={styles.itemMain}>
                  <div style={styles.itemTitleRow}>
                    <strong style={{ fontSize: '0.95rem' }}>{inv.label}</strong>
                    <span style={styles.badgePending}>
                      From {inv.shared_by_name || inv.owner_name || 'teammate'}
                    </span>
                  </div>
                  <div style={styles.itemUrl}>{inv.url}</div>
                </div>
                <div style={styles.itemActions}>
                  <button type="button" onClick={() => respondInvite(inv, 'accept')} style={styles.primaryBtn}>
                    Accept
                  </button>
                  <button type="button" onClick={() => respondInvite(inv, 'refuse')} style={styles.dangerBtn}>
                    Refuse
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={styles.listCard}>
        <div style={styles.listHeader}>Shared with you (accepted)</div>
        {loading ? (
          <p style={{ ...styles.muted, padding: '1rem' }}>Loading…</p>
        ) : acceptedShared.length === 0 ? (
          <p style={{ ...styles.muted, padding: '1rem' }}>
            No accepted shared bookmarks yet. When a teammate invites you, accept it above.
          </p>
        ) : (
          <ul style={styles.list}>{acceptedShared.map((bm) => renderBookmarkRow(bm, { shared: true }))}</ul>
        )}
      </div>

      <div style={{ ...styles.listCard, marginTop: 12 }}>
        <div style={styles.listHeader}>Your personal bookmarks</div>
        {loading ? (
          <p style={{ ...styles.muted, padding: '1rem' }}>Loading…</p>
        ) : personalOnly.length === 0 ? (
          <p style={{ ...styles.muted, padding: '1rem' }}>
            No personal bookmarks yet. Add one above, then Share… to invite teammates.
          </p>
        ) : (
          <ul style={styles.list}>{personalOnly.map((bm) => renderBookmarkRow(bm))}</ul>
        )}
      </div>

      {shareModal && (
        <div style={styles.helpOverlay} onClick={closeShareModal}>
          <div style={styles.shareModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.helpHeader}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>
                {shareModal.mode === 'all'
                  ? `Share all my bookmarks (${shareModal.count})`
                  : `Share “${shareModal.bookmark.label}”`}
              </h3>
              <button type="button" onClick={closeShareModal} style={styles.helpClose}>×</button>
            </div>
            <div style={styles.helpBody}>
              <p style={{ marginTop: 0 }}>
                {shareModal.mode === 'all' ? (
                  <>
                    Invite people to <strong>all {shareModal.count}</strong> of your bookmarks on this {scopeNoun}.
                    They must <strong>Accept</strong> before those show on their SharePoint page. Nobody is selected by default.
                  </>
                ) : (
                  <>
                    Pick who on this {scopeNoun} should get an invitation. They must <strong>Accept</strong> before
                    it appears on their SharePoint page. Nobody is selected by default.
                  </>
                )}
              </p>
              {members.length === 0 ? (
                <p style={styles.muted}>No other teammates are assigned to this {scopeNoun} yet.</p>
              ) : (
                <>
                  <div style={styles.shareToolbar}>
                    <button type="button" onClick={selectAllMembers} style={styles.secondaryBtn}>
                      Select all ({members.length})
                    </button>
                    <button type="button" onClick={() => setSelectedUserIds([])} style={styles.secondaryBtn}>
                      Clear
                    </button>
                  </div>
                  <ul style={styles.memberList}>
                    {members.map((m) => (
                      <li key={m.id} style={styles.memberRow}>
                        <label style={styles.memberLabel}>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(m.id)}
                            onChange={() => toggleUser(m.id)}
                          />
                          <span>
                            {m.name || m.email}
                            {m.name && m.email ? (
                              <span style={{ color: '#94a3b8', fontSize: 12 }}> · {m.email}</span>
                            ) : null}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div style={styles.shareFooter}>
                <button
                  type="button"
                  disabled={sharing || selectedUserIds.length === 0}
                  onClick={() => sendShare({ everyone: false })}
                  style={styles.primaryBtn}
                >
                  {sharing ? 'Sending…' : `Send invite (${selectedUserIds.length})`}
                </button>
                <button
                  type="button"
                  disabled={sharing || members.length === 0}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Invite everyone assigned to this ${scopeNoun} (${members.length} people)? They still need to accept.`
                      )
                    ) {
                      sendShare({ everyone: true });
                    }
                  }}
                  style={styles.secondaryBtn}
                >
                  Share with everyone…
                </button>
                <button type="button" onClick={closeShareModal} style={styles.secondaryBtn}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isBridge && (
        <SharePointAssistant
          projectId={projectId}
          estimateId={estimateId}
          scopeNoun={scopeNoun}
          onBookmarksChanged={fetchAll}
        />
      )}

      {showHelp && (
        <div style={styles.helpOverlay} onClick={() => setShowHelp(false)}>
          <div style={styles.helpModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.helpHeader}>
              <h3 style={{ margin: 0 }}>{productName} help</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton
                  compact
                  guideId={bridge?.helpId || 'sharepoint-browser-help'}
                  title={`${productName} Help`}
                  accent="#0f766e"
                  dark
                />
                <button type="button" onClick={() => setShowHelp(false)} style={styles.helpClose}>×</button>
              </div>
            </div>
            <div style={styles.helpBody}>
              <HelpWithVideoTour
                guideId={bridge?.helpId || 'sharepoint-browser-help'}
                title={`${productName} Help`}
                accent="#0f766e"
              >
              {isBridge ? (
                <>
                  <p>
                    <strong>Link the project</strong> — paste the {productName} Home URL.
                    We list Home, documents, drawings, RFIs, and the other tools.
                  </p>
                  <p>
                    <strong>Open in {productName}</strong> — write the RFI and mark up still happen there.
                    The portal is the shortcut list, not a copy of their product. Progress colour stays on Schedule → Model.
                  </p>
                  <p>
                    <strong>Star to bookmark</strong> — click the star left of Open on any tool, or paste any link below.
                    Share works the same as SharePoint bookmarks.
                  </p>
                </>
              ) : (
                <>
              <p>
                <strong>Browse in the portal</strong> — open folders, preview PDFs and images, and download files without leaving LGL Tools.
              </p>
              <p>
                <strong>Star to bookmark</strong> — click the star to the left of Open on any folder or file. Click again to remove it.
              </p>
              <p>
                <strong>Open in SharePoint</strong> — use this when you need to edit Word/Excel, upload, or manage permissions.
              </p>
              <p>
                <strong>Sharing bookmarks</strong> — bookmarks start as yours alone. Share one with Share…, or Share all…
                to invite teammates to every bookmark at once. They must Accept before it shows on their list.
              </p>
              <p>
                <strong>Download / restore backup</strong> — optional safety copy of your bookmark list (a simple text file).
                Most people only need Share. Restore puts bookmarks back on your personal list.
              </p>
              <p>
                Project pages use the job site URL. Estimate pages use the Estimates library folder. They are never swapped.
              </p>
                </>
              )}
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: {
    padding: '1.5rem',
    maxWidth: 1100,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  helpBtn: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '1px solid #cbd5e1',
    background: '#fff',
    cursor: 'pointer',
    fontWeight: 700,
    color: '#0f766e',
  },
  title: {
    margin: 0,
    fontSize: '1.35rem',
    color: '#1a1a1a',
  },
  sub: {
    margin: '0.35rem 0 0',
    color: '#6c757d',
    fontSize: '0.9rem',
    maxWidth: 560,
  },
  muted: {
    color: '#6c757d',
    fontSize: '0.9rem',
    margin: 0,
  },
  noRoot: {
    fontSize: '0.85rem',
    color: '#6c757d',
    fontStyle: 'italic',
    maxWidth: 280,
    textAlign: 'right',
  },
  browseBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    padding: '8px 12px',
    marginBottom: 10,
    background: '#ecfdf5',
    border: '1px solid #a7f3d0',
    borderRadius: 6,
    fontSize: 13,
    color: '#065f46',
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#0f766e',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: 13,
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#842029',
    border: '1px solid #f5c2c7',
    borderRadius: 6,
    padding: '0.65rem 0.85rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  notice: {
    backgroundColor: '#d1e7dd',
    color: '#0f5132',
    border: '1px solid #badbcc',
    borderRadius: 6,
    padding: '0.65rem 0.85rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  form: {
    backgroundColor: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: 8,
    padding: '1rem',
    marginBottom: '0.75rem',
  },
  formRow: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  formHint: {
    margin: '10px 0 0',
    fontSize: 12,
    color: '#64748b',
  },
  field: {
    flex: 1,
    minWidth: 160,
  },
  label: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#495057',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '0.55rem 0.7rem',
    border: '1px solid #ced4da',
    borderRadius: 6,
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  formActions: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  primaryBtn: {
    display: 'inline-block',
    padding: '0.55rem 0.9rem',
    backgroundColor: '#0d6efd',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.9rem',
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  secondaryBtn: {
    padding: '0.45rem 0.75rem',
    backgroundColor: '#f8f9fa',
    color: '#333',
    border: '1px solid #ced4da',
    borderRadius: 6,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  dangerBtn: {
    padding: '0.45rem 0.75rem',
    backgroundColor: '#fff',
    color: '#dc3545',
    border: '1px solid #f1aeb5',
    borderRadius: 6,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  listCard: {
    backgroundColor: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: 8,
    overflow: 'hidden',
  },
  listHeader: {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #e9ecef',
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#333',
    backgroundColor: '#f8f9fa',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '0.85rem 1rem',
    borderBottom: '1px solid #f1f3f5',
  },
  itemMain: {
    minWidth: 0,
    flex: 1,
  },
  itemTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  itemLinkBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#0d6efd',
    fontWeight: 600,
    fontSize: '0.95rem',
    cursor: 'pointer',
    textAlign: 'left',
  },
  itemUrl: {
    marginTop: 2,
    fontSize: '0.78rem',
    color: '#868e96',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemActions: {
    display: 'flex',
    gap: '0.4rem',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  badgeMine: {
    fontSize: 11,
    fontWeight: 600,
    color: '#475569',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: 999,
    padding: '1px 8px',
  },
  badgeOther: {
    fontSize: 11,
    fontWeight: 600,
    color: '#1e40af',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 999,
    padding: '1px 8px',
  },
  badgePending: {
    fontSize: 11,
    fontWeight: 600,
    color: '#92400e',
    background: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: 999,
    padding: '1px 8px',
  },
  helpOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.45)',
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  helpModal: {
    background: '#fff',
    borderRadius: 10,
    width: 520,
    maxWidth: '96vw',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
  },
  shareModal: {
    background: '#fff',
    borderRadius: 10,
    width: 520,
    maxWidth: '96vw',
    maxHeight: '85vh',
    overflow: 'auto',
    boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
  },
  helpHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    background: '#0f766e',
    color: '#fff',
  },
  helpClose: {
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: 22,
    cursor: 'pointer',
    lineHeight: 1,
  },
  helpBody: {
    padding: '14px 18px 18px',
    fontSize: 14,
    color: '#334155',
    lineHeight: 1.5,
  },
  shareToolbar: {
    display: 'flex',
    gap: 8,
    marginBottom: 10,
  },
  memberList: {
    listStyle: 'none',
    margin: '0 0 14px',
    padding: 0,
    maxHeight: 260,
    overflow: 'auto',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
  },
  memberRow: {
    borderBottom: '1px solid #f1f5f9',
  },
  memberLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: 13,
  },
  shareFooter: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
};

export default SharePointBookmarksPage;
